// import * as React from "react";
// import { Box, Paper, Typography, IconButton, Button } from "@mui/material";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
// import dayjs, { Dayjs } from "dayjs";
// import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
// import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// import { UseFormSetValue } from "react-hook-form";

// interface SlotAvailabilityCalendarProps {
//   setValue: UseFormSetValue<any>;
// }

// const SlotAvailabilityCalendar: React.FC<SlotAvailabilityCalendarProps> = ({
//   setValue,
// }) => {
//   const [selectedDates, setSelectedDates] = React.useState<Dayjs[]>([]);
//   const [currentMonth, setCurrentMonth] = React.useState(dayjs());

//  const toggleDate = (date: Dayjs) => {
//   setSelectedDates((prev) => {
//     const updated = prev.some((d) => d.isSame(date, "day"))
//       ? prev.filter((d) => !d.isSame(date, "day"))
//       : [...prev, date];

//     if (updated.length > 0) {
//       const sorted = [...updated].sort((a, b) => 
//         a.isAfter(b) ? 1 : -1
//       );
//       // Format like your LabSlot data: "2025-12-19T00:00:00"
//       const firstDate = sorted[0].toISOString().split('T')[0] + 'T00:00:00';
      
//       setValue("date", firstDate, { shouldDirty: true });
//       console.log("✅ Set date:", firstDate);
//     } else {
//       setValue("date", undefined, { shouldDirty: true });
//       console.log("❌ Cleared date");
//     }

//     return updated;
//   });
// };



//   const headerText = React.useMemo(() => {
//     if (selectedDates.length === 0) {
//       return "No date selected";
//     }

//     if (selectedDates.length === 1) {
//       return selectedDates[0].format("ddd, MMM DD");
//     }

//     const sorted = [...selectedDates].sort((a, b) =>
//       a.isAfter(b) ? 1 : -1
//     );

//     return `${sorted[0].format("ddd, MMM DD")} – ${sorted[
//       sorted.length - 1
//     ].format("ddd, MMM DD")}`;
//   }, [selectedDates]);

//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         p: 2.5,
//         borderRadius: "12px",
//         backgroundColor: "#F3FBF8",
//       }}
//     >
//       {/* Title */}
//       <Typography fontWeight={600} sx={{ mb: 0.5 }}>
//         Calendar – Slot Availability
//       </Typography>

//       {/* ✅ Selected Date Display */}
//       <Typography
//         variant="body2"
//         sx={{ mb: 2, color: "#098963", fontWeight: 600 }}
//       >
//         Selected Date(s): {headerText}
//       </Typography>

//       {/* Month Navigation */}
//       <Box
//         display="flex"
//         alignItems="center"
//         justifyContent="space-between"
//         mb={1}
//       >
//         <IconButton onClick={() => setCurrentMonth((m) => m.subtract(1, "month"))}>
//           <ChevronLeftIcon />
//         </IconButton>

//         <Typography fontWeight={600}>
//           {currentMonth.format("MMMM YYYY")}
//         </Typography>

//         <IconButton onClick={() => setCurrentMonth((m) => m.add(1, "month"))}>
//           <ChevronRightIcon />
//         </IconButton>
//       </Box>

//       {/* Calendar */}
//       <LocalizationProvider dateAdapter={AdapterDayjs}>
//         <DateCalendar
//           value={null}
//           referenceDate={currentMonth}
//           onChange={(date) => date && toggleDate(date)}
//           slots={{
//             day: (props) => {
//               const isSelected = selectedDates.some((d) =>
//                 d.isSame(props.day, "day")
//               );

//               return (
//                 <Box
//                   onClick={() => toggleDate(props.day)}
//                   sx={{
//                     width: 36,
//                     height: 36,
//                     borderRadius: "50%",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     cursor: "pointer",
//                     backgroundColor: isSelected ? "#098963" : "transparent",
//                     color: isSelected ? "#fff" : "#333",
//                     "&:hover": {
//                       backgroundColor: "#CDEBE2",
//                     },
//                   }}
//                 >
//                   {props.day.date()}
//                 </Box>
//               );
//             },
//           }}
//         />
//       </LocalizationProvider>

//       {/* Footer */}
//       <Box display="flex" justifyContent="flex-end" mt={2}>
//         <Button size="small" onClick={() => setSelectedDates([])}>
//           Clear
//         </Button>
//       </Box>
//     </Paper>
//   );
// };

// export default SlotAvailabilityCalendar;



import * as React from "react";
import { Box, Paper, Typography, IconButton, Button } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs, { Dayjs } from "dayjs";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface SlotAvailabilityCalendarProps {
  // Every date in the selected range, as "YYYY-MM-DDT00:00:00" (empty when nothing is selected)
  onDatesChange: (dates: string[]) => void;
}

// Click a start date, then an end date; every day in between is included.
// A third click starts a new range. Past dates can't be picked.
const SlotAvailabilityCalendar: React.FC<SlotAvailabilityCalendarProps> = ({ onDatesChange }) => {
  const [rangeStart, setRangeStart] = React.useState<Dayjs | null>(null);
  const [rangeEnd, setRangeEnd] = React.useState<Dayjs | null>(null);
  const [currentMonth, setCurrentMonth] = React.useState(dayjs());

  const today = dayjs().startOf("day");

  // All days from start to end (or just the start while the end isn't picked yet)
  const selectedDays = React.useMemo(() => {
    if (!rangeStart) return [] as Dayjs[];
    const end = rangeEnd || rangeStart;
    const days: Dayjs[] = [];
    for (let d = rangeStart; !d.isAfter(end, "day"); d = d.add(1, "day")) days.push(d);
    return days;
  }, [rangeStart, rangeEnd]);

  React.useEffect(() => {
    onDatesChange(selectedDays.map((d) => d.format("YYYY-MM-DD") + "T00:00:00"));
    // onDatesChange is recreated by the parent on every render; only the dates matter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays]);

  const pickDate = (date: Dayjs) => {
    const day = date.startOf("day");
    if (day.isBefore(today)) return;
    if (!rangeStart || rangeEnd) {
      // Start a new range
      setRangeStart(day);
      setRangeEnd(null);
    } else if (day.isBefore(rangeStart, "day")) {
      setRangeEnd(rangeStart);
      setRangeStart(day);
    } else {
      setRangeEnd(day);
    }
  };

  const clear = () => {
    setRangeStart(null);
    setRangeEnd(null);
  };

  const headerText = React.useMemo(() => {
    if (!rangeStart) return "No date selected";
    if (selectedDays.length === 1) {
      return `${rangeStart.format("ddd, MMM DD")}${rangeEnd ? "" : " (pick an end date for a range)"}`;
    }
    return `${rangeStart.format("ddd, MMM DD")} – ${(rangeEnd || rangeStart).format("ddd, MMM DD")} (${selectedDays.length} days)`;
  }, [rangeStart, rangeEnd, selectedDays]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "12px",
        backgroundColor: "#F3FBF8",
      }}
    >
      {/* Title */}
      <Typography fontWeight={600} sx={{ mb: 0.5 }}>
        Calendar – Slot Availability
      </Typography>

      {/* Selected range */}
      <Typography variant="body2" sx={{ mb: 2, color: "#098963", fontWeight: 600 }} aria-live="polite">
        Selected Date(s): {headerText}
      </Typography>

      {/* Month Navigation */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <IconButton aria-label="Previous month" onClick={() => setCurrentMonth((m) => m.subtract(1, "month"))}>
          <ChevronLeftIcon />
        </IconButton>

        <Typography fontWeight={600}>{currentMonth.format("MMMM YYYY")}</Typography>

        <IconButton aria-label="Next month" onClick={() => setCurrentMonth((m) => m.add(1, "month"))}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Calendar */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateCalendar
          value={null}
          referenceDate={currentMonth}
          minDate={today}
          onChange={(date) => date && pickDate(date)}
          slots={{
            day: (props) => {
              const day = props.day;
              const isPast = day.isBefore(today, "day");
              const isEndpoint =
                (rangeStart && day.isSame(rangeStart, "day")) || (rangeEnd && day.isSame(rangeEnd, "day"));
              const isInRange = selectedDays.some((d) => d.isSame(day, "day"));

              return (
                <Box
                  role="button"
                  tabIndex={isPast ? -1 : 0}
                  aria-label={day.format("dddd, D MMMM YYYY")}
                  aria-pressed={isInRange}
                  aria-disabled={isPast}
                  onClick={() => pickDate(day)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      pickDate(day);
                    }
                  }}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isPast ? "default" : "pointer",
                    backgroundColor: isEndpoint ? "#098963" : isInRange ? "#CDEBE2" : "transparent",
                    color: isEndpoint ? "#fff" : isPast ? "#BDBDBD" : "#333",
                    "&:hover": isPast ? {} : { backgroundColor: isEndpoint ? "#077352" : "#CDEBE2" },
                    "&:focus-visible": { outline: "2px solid #098963" },
                  }}
                >
                  {day.date()}
                </Box>
              );
            },
          }}
        />
      </LocalizationProvider>

      {/* Footer */}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button size="small" type="button" onClick={clear}>
          Clear
        </Button>
      </Box>
    </Paper>
  );
};

export default SlotAvailabilityCalendar;
